import OrganizationSwitcher from "@/components/organization/OrganizationSwitcher";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { featureTemplateService } from "@/services/featureTemplateService";
import { FeatureTemplateWithDetails } from "@/types/featureTemplate";
import {
  Activity,
  Bot,
  Calendar,
  CreditCard,
  FileJson,
  FileUp,
  LayoutDashboard,
  Lock,
  LogOut,
  MessageSquareText,
  Phone,
  Play,
  Plus,
  Settings,
  Sparkles,
  Users,
  UserCircle,
  Workflow,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../supabase/auth";
import { useOrganization } from "../../../App";
import { FeatureGuard } from "./FeatureGuard";
import PageHeader from "./PageHeader";

interface ActionButtonConfig {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  activeItem?: string;
  showCreateButton?: boolean;
  title?: string;
  description?: string;
  actionButtons?: ActionButtonConfig[];
}

interface NavItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  requiresPro: boolean;
  isTemplate?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { icon: <LayoutDashboard />, label: "Dashboard", href: "/dashboard", requiresPro: false },
  { icon: <Bot />, label: "Assistants", href: "/assistants", requiresPro: false },
  { icon: <UserCircle />, label: "Personas", href: "/personas", requiresPro: false },
  { icon: <Play />, label: "Simulators", href: "/simulators", requiresPro: false },
  { icon: <MessageSquareText />, label: "Prompt Engine", href: "/prompt-engine", requiresPro: false },
  { icon: <Sparkles />, label: "Micro Prompts", href: "/micro-prompts", requiresPro: false },
  { icon: <Users />, label: "Leads", href: "/leads", requiresPro: false },
  { icon: <FileUp />, label: "Lead Uploads", href: "/lead-uploads", requiresPro: false },
  { icon: <Settings />, label: "Providers", href: "/providers", requiresPro: false },
  { icon: <CreditCard />, label: "Credits Balance", href: "/credits-balance", requiresPro: false },
  { icon: <Calendar />, label: "Analytics", href: "/analytics", requiresPro: true },
  { icon: <Phone />, label: "Phone Numbers", href: "/phone-numbers", requiresPro: true },
  { icon: <Phone className="rotate-180" />, label: "Call Requests", href: "/call-requests", requiresPro: false },
  { icon: <FileJson />, label: "Schema Templates", href: "/schema-templates", requiresPro: false },
  { icon: <MessageSquareText />, label: "Call Logs", href: "/call-logs", requiresPro: false },
  { icon: <Activity />, label: "Latency Analytics", href: "/analytics/latency", requiresPro: false },
  { icon: <Calendar />, label: "Scheduled Calls", href: "/scheduled-calls", requiresPro: false },
  { icon: <Workflow />, label: "CallFlows", href: "/callflows", requiresPro: false },
  { icon: <MessageSquareText />, label: "Slots", href: "/slots", requiresPro: false },
  { icon: <FileJson />, label: "Feature Templates", href: "/feature-templates", requiresPro: false },
];

const DashboardLayout = ({
  children,
  activeItem = "Dashboard",
  showCreateButton = false,
  title,
  description,
  actionButtons = [],
}: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [templates, setTemplates] = useState<FeatureTemplateWithDetails[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const { currentOrganization } = useOrganization();

  const currentPath = location.pathname;

  const getCacheKey = () =>
    currentOrganization ? `templates_${currentOrganization.id}` : null;

  const cleanupStaleCache = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("templates_")) {
          const data = localStorage.getItem(key);
          if (data) {
            const { timestamp } = JSON.parse(data);
            if (Date.now() - timestamp > 60 * 60 * 1000) {
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.error("Error cleaning up cache:", error);
    }
  };

  useEffect(() => {
    cleanupStaleCache();

    if (currentOrganization) {
      const cacheKey = getCacheKey();
      if (cacheKey) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { templates: cachedTemplates, timestamp } = JSON.parse(cachedData);
            const isExpired = Date.now() - timestamp > 5 * 60 * 1000;
            if (!isExpired) {
              setTemplates(cachedTemplates);
            }
          } catch (error) {
            console.error("Error parsing cached templates:", error);
          }
        }
      }
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (user && currentOrganization) {
      fetchTemplates();
    }
  }, [user, currentOrganization]);

  const fetchTemplates = async () => {
    if (!currentOrganization) return;

    const cacheKey = getCacheKey();
    const hasCachedData = cacheKey && localStorage.getItem(cacheKey);

    if (!hasCachedData) {
      setIsLoadingTemplates(true);
    }

    try {
      const data = await featureTemplateService.getFeatureTemplates(
        currentOrganization.id,
      );
      setTemplates(data);

      if (cacheKey) {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ templates: data, timestamp: Date.now() }),
        );
      }
    } catch (error) {
      console.error("Error fetching feature templates:", error);
    } finally {
      if (!hasCachedData) {
        setIsLoadingTemplates(false);
      }
    }
  };

  const templateItems: NavItem[] = templates.map((template) => ({
    icon: <FileJson />,
    label: template.name,
    href: `/u/${template.name}`,
    requiresPro: false,
    isTemplate: true,
  }));

  const handleCreateAssistant = () => navigate("/assistant-dashboard");

  const isItemActive = (item: NavItem) =>
    item.label === activeItem ||
    (item.isTemplate && currentPath.startsWith(`/u/${item.label}`));

  const featureIdFor = (item: NavItem) =>
    item.isTemplate
      ? "featuretemplates"
      : item.label.toLowerCase().replace(/\s+/g, "");

  const renderNavItem = (item: NavItem) => (
    <FeatureGuard
      key={`${item.label}-${item.href}`}
      featureId={featureIdFor(item)}
      fallback={
        <SidebarMenuItem>
          <SidebarMenuButton
            disabled
            tooltip={`${item.label} (locked)`}
            className="opacity-60 cursor-not-allowed"
          >
            {item.icon}
            <span className="truncate">{item.label}</span>
            <Lock className="ml-auto size-3" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      }
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.label}
          isActive={isItemActive(item)}
          onClick={() => navigate(item.href)}
        >
          {item.icon}
          <span className="truncate">{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </FeatureGuard>
  );

  const userInitial =
    (user?.email?.[0] ?? "U").toUpperCase();

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" variant="sidebar">
        <SidebarHeader className="h-auto py-2">
          <div className="flex items-center gap-2 px-2">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground font-display text-sm font-semibold leading-none">
              V
            </div>
            <span className="inline-flex h-7 items-center font-display text-base font-semibold leading-none tracking-tight group-data-[collapsible=icon]:hidden">
              VOXI
            </span>
          </div>
          <div className="group-data-[collapsible=icon]:hidden">
            <OrganizationSwitcher />
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>General</SidebarGroupLabel>
            <SidebarMenu>
              {NAV_ITEMS.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroup>

          {(isLoadingTemplates || templateItems.length > 0) && (
            <SidebarGroup>
              <SidebarGroupLabel>Templates</SidebarGroupLabel>
              <SidebarMenu>
                {isLoadingTemplates && templateItems.length === 0
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <SidebarMenuItem key={`skeleton-${i}`}>
                        <SidebarMenuSkeleton showIcon />
                      </SidebarMenuItem>
                    ))
                  : templateItems.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    tooltip={user?.email ?? "Account"}
                    className="data-[state=open]:bg-sidebar-accent"
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary text-xs font-semibold">
                      {userInitial}
                    </span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="top" className="w-56">
                  <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
                    {user?.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/team")}>
                    <Users className="size-4" />
                    <span>Team</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/organization-settings")}>
                    <Settings className="size-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={signOut}>
                    <LogOut className="size-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="@container/content h-svh overflow-y-auto overscroll-y-none">
        <header className="sticky top-0 z-50 border-b bg-background">
          <div className="flex h-full items-center gap-2 px-4 py-3">
            <SidebarTrigger className="-ms-1" />
            <Separator orientation="vertical" className="h-4" />
            <span className="font-display text-sm font-semibold tracking-tight">
              {activeItem}
            </span>
            <div className="ms-auto flex items-center gap-2">
              <ThemeToggle />
            </div>
          </div>
        </header>

        {(title || description) && (
          <PageHeader
            title={title || activeItem}
            description={description}
            showCreateButton={showCreateButton}
            createButtonLabel="Create Assistant"
            onCreateClick={handleCreateAssistant}
            actionButtons={actionButtons}
          />
        )}

        <main className="w-full px-4 py-6 sm:px-6">
          {showCreateButton && !title && (
            <Button
              onClick={handleCreateAssistant}
              className="mb-6 w-full font-medium md:hidden"
            >
              <Plus className="size-4" />
              Create Assistant
            </Button>
          )}

          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default DashboardLayout;
